import { Adequation, CritereAdequation, EtatCritere } from '../../lib/useMatching';

/**
 * Avertissements du moteur, mis en clair.
 *
 * Le moteur rend des codes pour que l'API et les tests s'y accrochent ; l'écran
 * ne les affiche pas tels quels — « SANS_SOLUTION_DE_TRANSPORT » n'est pas une
 * phrase qu'on met sous les yeux d'un chargé de relation entreprise.
 */
const LIBELLES_AVERTISSEMENTS: Record<string, string> = {
  MOBILITE_NON_RENSEIGNEE: 'Mobilité non renseignée dans le dossier',
  SANS_SOLUTION_DE_TRANSPORT: 'Aucun moyen de déplacement déclaré pour un poste hors de la commune',
  DESSERTE_A_VERIFIER: 'Desserte en transports à vérifier',
  DISPONIBILITE_DEDUITE_DE_LA_DATE_ENTREE:
    "Disponibilité déduite de la date d'entrée en formation, faute de saisie en entretien — plafonnée au palier médian",
};

function libelleAvertissement(code: string): string {
  return LIBELLES_AVERTISSEMENTS[code] ?? code;
}

/**
 * État d'un critère, avec repli sur les propositions enregistrées avant que
 * l'état n'existe : `points === null` y signifiait « non renseigné ».
 */
function etatCritere(critere: CritereAdequation): EtatCritere {
  if (critere.etat) return critere.etat;
  return critere.points === null ? 'NON_RENSEIGNE' : 'MESURE';
}

export function MatchingDetailPanel({
  adequation,
  libelles = {},
}: {
  adequation: Adequation;
  /** Libellés rendus par l'API, indexés par le nom de la ligne de détail. */
  libelles?: Record<string, string>;
}) {
  const pointsAttention = adequation.avertissements ?? [];
  const nonEvaluables = adequation.criteresNonEvaluables ?? [];
  const couverture = adequation.couverture;

  return (
    <div className="flex flex-col gap-4 mt-3 pt-3 border-t border-gray-100">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {adequation.criteres.map((crit) => {
          const etat = etatCritere(crit);
          return (
            <div
              key={crit.critere}
              className="flex flex-col gap-1 p-3 bg-gray-50 rounded-lg border border-gray-100"
            >
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                {libelles[crit.critere] ?? crit.critere}
              </span>
              <div className="flex items-baseline gap-1 flex-wrap">
                {etat === 'NON_EVALUABLE' ? (
                  <span className="text-xs font-semibold text-gray-500">Non évaluable</span>
                ) : (
                  <>
                    <span
                      className={
                        etat === 'NON_RENSEIGNE'
                          ? 'text-sm font-black text-orange-500'
                          : 'text-sm font-black text-codialis-navy'
                      }
                    >
                      {crit.points ?? 0}
                    </span>
                    <span className="text-[10px] font-bold text-gray-400">/ {crit.maximum}</span>
                    {etat === 'NON_RENSEIGNE' && (
                      <span className="text-[10px] font-semibold text-orange-500">
                        · non renseigné
                      </span>
                    )}
                    {etat === 'MESURE' && crit.source === 'DEDUITE' && (
                      <span className="text-[10px] font-semibold text-amber-600">· déduite</span>
                    )}
                  </>
                )}
              </div>
              <span className="text-[10px] text-gray-500 leading-tight mt-1">{crit.motif}</span>
            </div>
          );
        })}
      </div>

      <div className="text-sm text-gray-700 bg-white border border-gray-100 rounded-lg p-4 shadow-sm">
        <h5 className="font-bold text-codialis-navy mb-3 text-xs uppercase tracking-wider flex justify-between items-start gap-2 flex-wrap">
          Pourquoi cette proposition&nbsp;?
          <span className="text-[10px] bg-codialis-sky/10 text-codialis-sky px-2 py-1 rounded-full normal-case tracking-normal font-semibold">
            {couverture
              ? `${couverture.mesures}/${couverture.retenus} critères évaluables`
              : adequation.confiance}
          </span>
        </h5>

        <p className="text-[11px] text-gray-500 mb-3">
          Score sur {adequation.maximumApplicable} points de barème
          {nonEvaluables.length > 0
            ? ` — ${nonEvaluables.join(', ')} hors calcul, faute de référentiel.`
            : '.'}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            {adequation.contexte && adequation.contexte.length > 0 ? (
              <div className="mb-2">
                <span className="font-semibold text-[11px] text-gray-500 uppercase tracking-wide">
                  Contexte favorable&nbsp;:
                </span>
                <ul className="list-disc pl-4 text-xs mt-1.5 space-y-1">
                  {adequation.contexte.map((ctx, i) => (
                    <li key={i}>{ctx}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <span className="text-xs text-gray-400">Aucun contexte particulier.</span>
            )}
          </div>
          <div>
            {pointsAttention.length > 0 && (
              <div className="mb-2">
                <span className="font-semibold text-[11px] text-orange-600 uppercase tracking-wide">
                  Points d'attention&nbsp;:
                </span>
                <ul className="list-disc pl-4 text-xs mt-1.5 space-y-1 text-orange-700">
                  {pointsAttention.map((av, i) => (
                    <li key={i}>{libelleAvertissement(av)}</li>
                  ))}
                </ul>
              </div>
            )}

            {adequation.criteresNonRenseignes && adequation.criteresNonRenseignes.length > 0 && (
              <div className="mb-2">
                <span className="font-semibold text-[11px] text-orange-600 uppercase tracking-wide">
                  Données manquantes&nbsp;:
                </span>
                <p className="text-xs text-orange-700 mt-1">
                  {adequation.criteresNonRenseignes.join(', ')} — comptés 0 point, leur poids reste
                  au dénominateur.
                </p>
              </div>
            )}

            {nonEvaluables.length > 0 && (
              <div className="mb-2">
                <span className="font-semibold text-[11px] text-gray-500 uppercase tracking-wide">
                  Non évaluables&nbsp;:
                </span>
                <p className="text-xs text-gray-600 mt-1">
                  {nonEvaluables.join(', ')} — hors du calcul, le référentiel ne sait pas les
                  comparer.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {adequation.motifsIneligibilite && adequation.motifsIneligibilite.length > 0 && (
        <div className="bg-red-50 text-red-800 p-3 rounded-lg border border-red-100 text-xs">
          <span className="font-bold uppercase tracking-wide block mb-1">Motifs de blocage&nbsp;:</span>
          <ul className="list-disc pl-4">
            {adequation.motifsIneligibilite.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
